import type { RootState } from '@/store';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import { estimateStateClosingTaxes } from '@/utils/fees/stateClosingTaxes';
import type { CalculateClosingDisclosureProps } from '.';
import { calculateEnhancedInterestRate } from './rateIntegration';

const calculateHELOANInterestRate = (
  baseRate: number,
  creditScore: number,
  ltv: number,
  reduxState?: RootState
) => {
  // HELOAN rates are typically fixed and higher than primary mortgages
  if (reduxState) {
    return calculateEnhancedInterestRate(
      reduxState,
      'HELOAN',
      baseRate,
      creditScore,
      ltv
    );
  }
  return baseRate;
};

export const calculateHELOANClosingDisclosure = (
  props: CalculateClosingDisclosureProps & Record<string, any>,
  reduxState?: RootState
) => {
  const {
    purchasePrice: purchasePriceProp,
    offerPrice,
    listPrice,
    downPayment,
    creditScore,
    propertyType,
    occupancy,
    state: propertyState,
    county,
    city,
    zipCode,
    propertyTaxRate,
    homeownersInsuranceRate,
    hoaFees,
    currentLoanBalance,
    requestedLoanAmount,
    marketInterestRate = 8.0, // HELOAN rates are typically higher
  } = props as any;

  // HELOAN specific calculations
  const heloanAmount = requestedLoanAmount || 50000; // Default $50k loan
  // Callers pass offerPrice/listPrice (the home's value); purchasePrice
  // was never set, so every value-based figure came out NaN.
  const purchasePrice =
    Number(purchasePriceProp ?? offerPrice ?? listPrice) || 0;
  const loanAmount = purchasePrice - (downPayment || 0);
  // Price on combined LTV: existing first mortgage plus this new lien.
  const existingBalance = Number(currentLoanBalance);
  const ltv = purchasePrice
    ? ((Number.isFinite(existingBalance) && existingBalance > 0
        ? existingBalance
        : loanAmount) +
        heloanAmount) /
      purchasePrice
    : 0.8;
  const heloanInterestRate = calculateHELOANInterestRate(
    marketInterestRate || 8.0,
    Number(creditScore) || 700,
    Number(ltv) || 0.8,
    reduxState
  );

  // HELOAN terms
  const loanTerm = 180; // 15 year term is common for HELOAN

  // HELOAN specific fees
  const appraisalFeeBase = 600; // Standard appraisal fee
  const processingFeeBase = 600; // Standard processing fee
  const underwritingFee = 400; // Standard underwriting fee
  const applicationFeeBase = 200; // Standard application fee

  // HELOAN specific fees
  const originationFee = heloanAmount * 0.01; // 1% origination fee

  // Title and settlement fees
  const titleSettlementAgentFee = 1000; // Standard title fee
  const titleLendersTitleInsuranceBase = (heloanAmount * 0.004).toFixed(2); // Standard rate

  // Recording fees
  const countyRecordingFees = 50;
  const stateRecordingFees = 50;
  const recordingTitle = 50;
  const recordingMortgage = 50;
  const recordingDeedBase = 50;

  // Transfer taxes (typically not applicable for HELOAN)
  const transferTaxes = 0;
  // A new lien still owes the borrower's state loan taxes (e.g. Florida
  // doc stamps + intangible tax) — see utils/fees/stateClosingTaxes.
  const stateTaxes = estimateStateClosingTaxes({
    zipCode,
    price: purchasePrice,
    loanAmount: heloanAmount,
    transaction: 'refinance',
  });
  const mortgageTaxes = stateTaxes.mortgageTaxes;

  // Prepaid costs (minimal for HELOAN)
  const prepaidsPropertyTaxes = 0; // No prepaid taxes for HELOAN
  const prepaidsHomeownersInsurance = 0; // No prepaid insurance for HELOAN
  const prepaidInterest =
    (heloanAmount * heloanInterestRate * 15) / (365 * 100); // 15 days

  // Monthly payment calculation
  const monthlyPrincipalInterest =
    (heloanAmount *
      (heloanInterestRate / 12 / 100) *
      Math.pow(1 + heloanInterestRate / 12 / 100, loanTerm)) /
    (Math.pow(1 + heloanInterestRate / 12 / 100, loanTerm) - 1);
  const monthlyPayment = monthlyPrincipalInterest;

  // Lender cost totals
  const lenderCostTotals =
    appraisalFeeBase +
    processingFeeBase +
    underwritingFee +
    applicationFeeBase +
    originationFee +
    titleSettlementAgentFee +
    parseFloat(titleLendersTitleInsuranceBase) +
    countyRecordingFees +
    stateRecordingFees +
    recordingTitle +
    recordingMortgage +
    recordingDeedBase +
    transferTaxes +
    mortgageTaxes;

  // Prepaid totals
  const prepaidTotals =
    prepaidsPropertyTaxes + prepaidsHomeownersInsurance + prepaidInterest;

  // Total closing costs
  const totalClosingCosts = lenderCostTotals + prepaidTotals;

  // Cash to close (minimal for HELOAN)
  const cashToClose = totalClosingCosts;

  return {
    type: USER_LOAN_PROGRAM.HELOAN,
    loanAmount: heloanAmount,
    interestRate: heloanInterestRate,
    monthlyPayment,
    monthlyPrincipalInterest,
    loanTerm,
    appraisalFeeBase,
    processingFeeBase,
    underwritingFee,
    applicationFeeBase,
    originationFee,
    titleSettlementAgentFee,
    titleLendersTitleInsuranceBase,
    countyRecordingFees,
    stateRecordingFees,
    recordingTitle,
    recordingMortgage,
    recordingDeedBase,
    transferTaxes,
    mortgageTaxes,
    stateTaxLines: stateTaxes.lines,
    stateTaxState: stateTaxes.state,
    prepaidsPropertyTaxes,
    prepaidsHomeownersInsurance,
    prepaidInterest,
    lenderCostTotals,
    prepaidTotals,
    totalClosingCosts,
    cashToClose,
    // HELOAN specific properties
    minimumOwnersTitle: 1650,
    maximumOwnersTitle: 5850,
    // The borrower already owns the home: no owner's title policy.
    optionalOwnersTitleInsurance: 0,
    minimumInspectionFee: 650,
    maximumInspectionFee: 4500,
    homeInspectionFee: Math.min(Math.max(500, 650), 4500),
    // Refinance specific properties
    currentLoanBalance,
    requestedLoanAmount: heloanAmount,
  };
};
